import {
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { AppRoutes } from '../../constants';
import { Icon } from '@iconify/react';
import { JSX, useState } from 'react';
import { removeAuthToken } from '../../lib/accessTokens';
import { useGetCurrentAgent, useQueryMe } from '../../hooks/auth';
import Content from '../content';
import Logo from '../logo';

export const DRAWER_WIDTH = 210;

export const doLogout = (): void => {
  removeAuthToken();
  // eslint-disable-next-line no-restricted-globals
  (top ? top.window : window).location.href = '/logout';
};

const listSx = {
  '& .MuiListItemButton-root': {
    px: 4,
    py: 1,
    color: 'common.gray',
  },
  '& .MuiListItemIcon-root': {
    minWidth: 24,
  },
  '& .MuiListItemText-root': {
    fontWeight: 500,
  },
};

export interface LayoutHeaderProps {
  breadcrumbs?: { label: string; href?: string }[];
}

const LayoutHeader = ({ breadcrumbs }: LayoutHeaderProps): JSX.Element => {
  const { data: userData } = useQueryMe();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpenProfileDropdown = Boolean(anchorEl);
  const { data: agent } = useGetCurrentAgent();
  const handleCloseProfileDropdown = (): void => {
    setAnchorEl(null);
  };
  const handleLogout = (): void => {
    handleCloseProfileDropdown();
    doLogout();
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };
  const drawer = (
    <>
      <Box className="m-4">
        <Logo />
      </Box>
      <List sx={listSx}>
        <ListItem disablePadding>
          <ListItemButton component={Link} href={AppRoutes.Dashboard}>
            <ListItemIcon>
              <Icon icon="uil:web-grid" />
            </ListItemIcon>
            <ListItemText primary="Dashboard" disableTypography />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} href={AppRoutes.Clients}>
            <ListItemIcon>
              <Icon icon="uil:users-alt" />
            </ListItemIcon>
            <ListItemText primary="Clients" disableTypography />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} href={AppRoutes.FNAs}>
            <ListItemIcon>
              <Icon icon="uil:chart-line" />
            </ListItemIcon>
            <ListItemText primary="Needs analyses" disableTypography />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} href={AppRoutes.Quotes}>
            <ListItemIcon>
              <Icon icon="uil:file-landscape-alt" />
            </ListItemIcon>
            <ListItemText primary="Quotes" disableTypography />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} href={AppRoutes.Reports}>
            <ListItemIcon>
              <Icon icon="uil:file-edit-alt" />
            </ListItemIcon>
            <ListItemText primary="Reports" disableTypography />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );
  return (
    <>
      <AppBar position="static" elevation={0} color="transparent" className="mb-4">
        <Toolbar disableGutters>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ ml: 2, display: { sm: 'none' } }}
          >
            <Icon icon="material-symbols:menu" />
          </IconButton>
          <Content sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Breadcrumbs separator="/" aria-label="breadcrumb">
                <Link underline="hover" href="/">
                  Dashboard
                </Link>
                {breadcrumbs?.map(({ label, href }, index) => (
                  <Link
                    underline="hover"
                    key={label}
                    color={index === breadcrumbs.length - 1 ? 'primary.main' : 'inherit'}
                    href={href}
                  >
                    {label}
                  </Link>
                ))}
              </Breadcrumbs>
            </Box>
            <Box sx={{ color: 'text.secondary' }}>
              <Button
                size="large"
                color="inherit"
                endIcon={<Icon icon="uil:setting" />}
                onClick={(event): void => setAnchorEl(event.currentTarget)}
              >
                {agent?.brokerName}
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={isOpenProfileDropdown}
                onClose={handleCloseProfileDropdown}
              >
                {userData && (
                  <Box minWidth={200} className="px-4 py-2">
                    <Box>
                      <Typography sx={{ lineHeight: 1 }} color="text.secondary" variant="caption">
                        Logged in as:
                      </Typography>
                      <Typography sx={{ lineHeight: 1 }} fontWeight={500}>
                        {userData.optionalParameters.userFirstname}{' '}
                        {userData.optionalParameters.userLastname}
                      </Typography>
                    </Box>
                    <Box className="mt-3">
                      <Typography sx={{ lineHeight: 1 }} color="text.secondary" variant="caption">
                        Acting as:
                      </Typography>
                      <Typography sx={{ lineHeight: 1 }} fontWeight={500}>
                        {userData.optionalParameters.adviserFirstname}{' '}
                        {userData.optionalParameters.adviserSurname}
                      </Typography>
                      <Typography sx={{ lineHeight: 1 }} color="primary" variant="caption">
                        {userData.optionalParameters.adviserBrokerCode} |{' '}
                        {userData.optionalParameters.adviserHouseCode}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <Typography color="secondary">Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Content>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
};

export default LayoutHeader;
